import { create } from 'zustand';
import { coreApi } from '@/integrations/api/active';
import { supabase } from '@/integrations/supabase/client';
import { useOperatingStore } from '@/modules/operating/operating-store';
import { isGrantLive, slugify } from '@/modules/teams/types';
import type { BreakGlassGrant, EntityShare, ShareableEntityType, Team, TeamAccessEvent, TeamMember, TeamPrivacy, TeamRole } from '@/modules/teams/types';

/**
 * Team administration uses `team_access_events`, not domain audit rows:
 * audit-row delete restrictions would make teams undeletable, while root-level
 * audit labels could disclose secret team names.
 */
interface TeamsState {
  teams: Team[];
  members: TeamMember[];
  loading: boolean;
  workspaceId: string | null;
  error: string | null;
  /** Active-team cap from `workspace_team_limit`; null means unlimited. */
  teamLimit: number | null;
  /** RLS-filtered live break-glass holds loaded with the tree for immediate notices. */
  breakGlass: BreakGlassGrant[];
  /** Fetched per team, on demand — the log is a surface you open, not state the app carries. */
  accessEvents: TeamAccessEvent[];
  /** Workspace shares already filtered per viewer by RLS. */
  shares: EntityShare[];

  /** Drop another user's teams and grants — see workspace-store.resetForUserChange. */
  reset: () => void;
  /** Clear a rejection once another surface has presented it. */
  clearError: () => void;

  /** Enter via break-glass; a live grant is reused without extending its expiry. */
  enterWithBreakGlass: (teamId: string) => Promise<boolean>;
  relinquishBreakGlass: (teamId: string) => Promise<void>;
  fetchAccessEvents: (teamId: string) => Promise<void>;
  /** Replace the visible target set for one entity's shares. */
  setShares: (
    entityType: ShareableEntityType, entityId: string, teamIds: string[],
    /** Per-target clone permission (#165); absent means leave/insert as allowed. */
    clonableByTeam?: Record<string, boolean>,
  ) => Promise<boolean>;
  /** Revoke every share a clone materialised, in one act (#165). */
  revokeDerivedShares: (scorecardId: string) => Promise<boolean>;
  /** Re-read `entity_shares` — after a trigger has written rows the client did not. */
  refreshShares: () => Promise<void>;
  transferHome: (entityType: ShareableEntityType, entityId: string, teamId: string) => Promise<boolean>;
  /** Choose the board a team opens; the database validates ownership/readability. */
  setPrimaryScorecard: (teamId: string, scorecardId: string | null) => Promise<void>;
  /** Bounded retry for sharing immediately after a fire-and-forget optimistic create. */
  shareAfterCreate: (entityType: ShareableEntityType, entityId: string, teamIds: string[]) => Promise<void>;
  fetchTeams: (workspaceId: string) => Promise<void>;
  createTeam: (input: { name: string; description?: string; privacy: TeamPrivacy; parentId: string | null }) => Promise<Team | null>;
  renameTeam: (id: string, name: string) => Promise<void>;
  setDescription: (id: string, description: string) => Promise<void>;
  setPrivacy: (id: string, privacy: TeamPrivacy, parentId?: string | null) => Promise<void>;
  setParent: (id: string, parentId: string | null) => Promise<void>;
  setArchived: (id: string, archived: boolean) => Promise<void>;
  deleteTeam: (id: string) => Promise<void>;

  countOpenAssignments: (teamId: string, personId: string) => Promise<number | null>;
  /** Clear this person's open assignments on this team. Returns false on failure. */
  unassignInTeam: (teamId: string, personId: string) => Promise<boolean>;
  addMember: (teamId: string, userId: string, role: TeamRole) => Promise<void>;
  setMemberRole: (memberId: string, role: TeamRole) => Promise<void>;
  removeMember: (memberId: string) => Promise<void>;
}

function fromRow(row: any): Team {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    parentId: row.parent_id,
    name: row.name,
    slug: row.slug,
    description: row.description ?? null,
    privacy: row.privacy,
    isRoot: row.is_root,
    archived: row.archived ?? false,
    primaryScorecardId: row.primary_scorecard_id ?? null,
  };
}

let _teamsRequestGeneration = 0;
let _accessEventsRequestGeneration = 0;
let _sharesRequestGeneration = 0;

function grantFromRow(row: any): BreakGlassGrant {
  return {
    id: row.id,
    teamId: row.team_id,
    userId: row.user_id,
    grantedAt: row.granted_at,
    expiresAt: row.expires_at,
    relinquishedAt: row.relinquished_at ?? null,
  };
}

function eventFromRow(row: any): TeamAccessEvent {
  return {
    id: row.id,
    teamId: row.team_id,
    actorUserId: row.actor_user_id ?? null,
    eventType: row.event_type,
    subjectUserId: row.subject_user_id ?? null,
    detail: row.detail ?? null,
    createdAt: row.created_at,
  };
}

function memberFromRow(row: any): TeamMember {
  return { id: row.id, teamId: row.team_id, userId: row.user_id, role: row.role };
}

/** Mirrors `entity_team_id`'s mapping, for the six types a share may name. */
const SHAREABLE_TABLES: Record<ShareableEntityType, 'rocks' | 'tasks' | 'issues' | 'metrics' | 'ideas' | 'scorecards'> = {
  rock: 'rocks', task: 'tasks', issue: 'issues',
  metric: 'metrics', idea: 'ideas', scorecard: 'scorecards',
};

function shareFromRow(row: any): EntityShare {
  return {
    id: row.id,
    entityType: row.entity_type,
    entityId: row.entity_id,
    teamId: row.team_id,
    createdBy: row.created_by ?? null,
    createdAt: row.created_at,
    clonable: row.clonable ?? true,
    derivedFromScorecardId: row.derived_from_scorecard_id ?? null,
  };
}

/** Surface rejected permission writes and refetch because the local tree may be stale. */
async function guard<T>(
  op: PromiseLike<{ data?: T; error: { message: string } | null }>,
  set: (partial: Partial<TeamsState>) => void,
  get: () => TeamsState,
): Promise<{ ok: boolean; data: T | null }> {
  const { data, error } = await op;
  if (error) {
    set({ error: error.message });
    const ws = get().workspaceId;
    if (ws) await get().fetchTeams(ws);
    return { ok: false, data: null };
  }
  set({ error: null });
  // Successful deletes legitimately return null data; only `error` decides success.
  return { ok: true, data: (data ?? null) as T | null };
}

async function core(
  path: string,
  body: Record<string, unknown> = {},
  method: 'POST' | 'PATCH' | 'DELETE' = 'POST',
): Promise<{ data: any; error: { message: string } | null }> {
  const { data, error } = await coreApi(path, body, method);
  return { data: data?.[0] ?? null, error: error ? { message: error.details || error.message } : null };
}

/** Teams-owned server cache, keyed by `workspaceId`. */
export const useTeamsStore = create<TeamsState>((set, get) => ({
  teams: [],
  members: [],
  loading: false,
  workspaceId: null,
  error: null,
  teamLimit: null,
  breakGlass: [],
  accessEvents: [],
  shares: [],

  reset: () => {
    _teamsRequestGeneration += 1;
    _accessEventsRequestGeneration += 1;
    _sharesRequestGeneration += 1;
    set({
      teams: [], members: [], workspaceId: null, error: null, loading: false,
      teamLimit: null, breakGlass: [], accessEvents: [], shares: [],
    });
  },

  clearError: () => set({ error: null }),

  enterWithBreakGlass: async (teamId) => {
    const { workspaceId, breakGlass } = get();
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth.user?.id;
    if (!workspaceId || !userId) return false;

    // Already inside: walk back in, write nothing. An admin working on a team
    // goes on and off it, and re-notifying the team each time would train
    // its members to ignore the one notification that matters. The clock keeps
    // running from the first entry rather than restarting, so the window is a
    // window rather than a rolling lease nobody can predict the end of.
    if (breakGlass.some((g) => g.teamId === teamId && g.userId === userId && isGrantLive(g))) {
      return true;
    }

    const { ok, data } = await guard(core(`/teams/${teamId}/break-glass`, { workspace_id: workspaceId }), set, get);
    if (!ok || !data) return false;

    set((s) => ({
      breakGlass: [
        ...s.breakGlass.filter((g) => !(g.teamId === teamId && g.userId === userId)),
        grantFromRow(data),
      ],
    }));
    return true;
  },

  relinquishBreakGlass: async (teamId) => {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) return;
    set((s) => ({ breakGlass: s.breakGlass.filter((g) => !(g.teamId === teamId && g.userId === userId)) }));
    await guard(core(`/teams/${teamId}/break-glass`, {}, 'DELETE'), set, get);
  },

  fetchAccessEvents: async (teamId) => {
    const requestGeneration = ++_accessEventsRequestGeneration;
    const workspaceId = get().workspaceId;
    set({ accessEvents: [] });
    const { data } = await supabase
      .from('team_access_events').select('*')
      .eq('team_id', teamId)
      .order('created_at', { ascending: false })
      .limit(100);
    if (
      requestGeneration !== _accessEventsRequestGeneration
      || get().workspaceId !== workspaceId
    ) return;
    set({ accessEvents: (data ?? []).map(eventFromRow) });
  },

  fetchTeams: async (workspaceId: string) => {
    const requestGeneration = ++_teamsRequestGeneration;
    const switchingWorkspace = get().workspaceId !== workspaceId;
    set({
      loading: true,
      workspaceId,
      ...(switchingWorkspace ? {
        teams: [], members: [], teamLimit: null, breakGlass: [], accessEvents: [], shares: [], error: null,
      } : {}),
    });
    // Both are RLS-filtered: secret teams the caller cannot see never arrive,
    // so the tree renders what the database says exists and nothing is filtered
    // a second time in the client.
    const [{ data: teamRows }, { data: memberRows }, { data: limit }, { data: grantRows }, { data: shareRows }] = await Promise.all([
      supabase.from('teams').select('*').eq('workspace_id', workspaceId),
      supabase.from('team_members').select('id, team_id, user_id, role'),
      supabase.rpc('workspace_team_limit', { _workspace_id: workspaceId }),
      supabase.from('team_break_glass_grants').select('*').eq('workspace_id', workspaceId)
        .is('relinquished_at', null).gt('expires_at', new Date().toISOString()),
      supabase.from('entity_shares').select('*').eq('workspace_id', workspaceId),
    ]);
    if (
      requestGeneration !== _teamsRequestGeneration
      || get().workspaceId !== workspaceId
    ) return;
    const teams = (teamRows ?? []).map(fromRow);
    const visible = new Set(teams.map((s) => s.id));
    set({
      teams,
      // team_members is not workspace-scoped, so filter to teams we actually
      // fetched rather than carrying another workspace's rows.
      members: (memberRows ?? []).map(memberFromRow).filter((m) => visible.has(m.teamId)),
      teamLimit: limit ?? null,
      breakGlass: (grantRows ?? []).map(grantFromRow),
      // Not filtered to `visible`, deliberately, and the difference matters: a
      // share you can see always points at a team you can read, but the ENTITY
      // may be homed somewhere you cannot — that is the whole mechanism. Dropping
      // rows whose home is invisible would drop exactly the shares that do work.
      shares: (shareRows ?? []).map(shareFromRow),
      loading: false,
    });
  },

  shareAfterCreate: async (entityType, entityId, teamIds) => {
    if (!teamIds.length) return;

    // Wait for the row rather than retrying the share and reading the failure:
    // `guard` refetches the whole tree on every rejection, so a retry loop would
    // pay for the race five times over and leave an error on screen in between.
    for (let attempt = 0; attempt < 10; attempt++) {
      const { data } = await supabase
        .from(SHAREABLE_TABLES[entityType]).select('id').eq('id', entityId).maybeSingle();
      if (data) break;
      await new Promise((r) => setTimeout(r, 150));
    }
    await get().setShares(entityType, entityId, teamIds);
  },

  setPrimaryScorecard: async (teamId, scorecardId) => {
    set((s) => ({
      teams: s.teams.map((sp) => (sp.id === teamId ? { ...sp, primaryScorecardId: scorecardId } : sp)),
    }));
    await guard(core(`/records/teams/${teamId}`, { primary_scorecard_id: scorecardId }, 'PATCH'), set, get);
  },

  /**
   * Move an entity's home non-optimistically. Database triggers update access
   * events, primary pointers, and shares, so refetch their results after success.
   */
  transferHome: async (entityType, entityId, teamId) => {
    const { workspaceId } = get();
    if (!workspaceId) return false;

    const { ok } = await guard(
      core('/home-transfers', { entity_type: entityType, entity_id: entityId, team_id: teamId }),
      set, get,
    );
    if (!ok) return false;

    const { data } = await supabase.from('entity_shares').select('*').eq('workspace_id', workspaceId);
    set({ shares: (data ?? []).map(shareFromRow) });
    await get().fetchTeams(workspaceId);

    // Keep the entity's local home in sync without refetching the workspace.
    useOperatingStore.setState((s) => {
      const move = <T extends { id: string; teamId?: string }>(list: T[]) =>
        list.map((x) => (x.id === entityId ? { ...x, teamId } : x));
      switch (entityType) {
        case 'rock':      return { rocks: move(s.rocks) };
        case 'task':      return { tasks: move(s.tasks) };
        case 'issue':     return { issues: move(s.issues) };
        case 'metric':    return { metrics: move(s.metrics) };
        case 'idea':      return { ideas: move(s.ideas) };
        case 'scorecard': return { scorecards: move(s.scorecards) };
      }
    });
    return true;
  },

  refreshShares: async () => {
    const { workspaceId } = get();
    if (!workspaceId) return;
    const requestGeneration = ++_sharesRequestGeneration;
    const { data } = await supabase.from('entity_shares').select('*').eq('workspace_id', workspaceId);
    if (
      requestGeneration !== _sharesRequestGeneration
      || get().workspaceId !== workspaceId
    ) return;
    set({ shares: (data ?? []).map(shareFromRow) });
  },

  revokeDerivedShares: async (scorecardId) => {
    const { ok } = await guard(core(`/scorecards/${scorecardId}/derived-shares`, {}, 'DELETE'), set, get);
    if (ok) await get().refreshShares();
    return ok;
  },

  setShares: async (entityType, entityId, teamIds, clonableByTeam) => {
    const { workspaceId, shares } = get();
    if (!workspaceId) return false;

    const current = shares.filter((s) => s.entityType === entityType && s.entityId === entityId);
    const wanted = new Set(teamIds);
    const added = teamIds.filter((id) => !current.some((s) => s.teamId === id));
    const removed = current.filter((s) => !wanted.has(s.teamId));

    const { ok } = await guard(
      core('/shares', {
        workspace_id: workspaceId,
        entity_type: entityType,
        entity_id: entityId,
        team_ids: teamIds,
        clonable_by_team: clonableByTeam ?? {},
      }),
      set, get,
    );
    if (!ok) return false;

    // Refetch database-assigned and trigger-validated share rows.
    if (added.length || removed.length) {
      const { data } = await supabase.from('entity_shares').select('*').eq('workspace_id', workspaceId);
      set({ shares: (data ?? []).map(shareFromRow) });
    }
    return true;
  },

  createTeam: async ({ name, description, privacy, parentId }) => {
    const { workspaceId, teams } = get();
    if (!workspaceId) return null;
    const row = {
      workspace_id: workspaceId,
      name: name.trim(),
      description: description?.trim() || null,
      slug: slugify(name, teams.map((s) => s.slug)),
      privacy,
      // A secret team is always parentless — the CHECK constraint enforces it,
      // and sending a parent would fail the insert rather than be ignored.
      parent_id: privacy === 'secret' ? null : parentId,
      is_root: false,
    };
    const { ok, data: created } = await guard(core('/records/teams', row), set, get);
    if (!ok || !created) return null;
    const team = fromRow(created);

    // The creator-enrolment trigger runs outside the insert's returned snapshot.
    const { data: memberRows } = await supabase
      .from('team_members').select('id, team_id, user_id, role').eq('team_id', team.id);

    set((s) => ({
      teams: [...s.teams, team],
      members: [
        ...s.members.filter((m) => m.teamId !== team.id),
        ...(memberRows ?? []).map(memberFromRow),
      ],
    }));
    return team;
  },

  renameTeam: async (id, name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    set((s) => ({ teams: s.teams.map((x) => (x.id === id ? { ...x, name: trimmed } : x)) }));
    await guard(core(`/records/teams/${id}`, { name: trimmed }, 'PATCH'), set, get);
  },

  setDescription: async (id, description) => {
    // Empty means absent, not an empty string — a blank blurb should read as "none
    // set" everywhere downstream rather than rendering a zero-height line.
    const next = description.trim() || null;
    set((s) => ({ teams: s.teams.map((x) => (x.id === id ? { ...x, description: next } : x)) }));
    await guard(core(`/records/teams/${id}`, { description: next }, 'PATCH'), set, get);
  },

  setPrivacy: async (id, privacy, parentId) => {
    // Both directions move the parent, because the constraints pull opposite
    // ways: teams_secret_no_parent_check forbids a secret team having a
    // parent, and teams_parent_required_check requires every other non-root
    // team to have one. Coming OUT of secret with a null parent is rejected,
    // which is why the caller must say where it rejoins the tree.
    const nextParent = privacy === 'secret' ? null : (parentId ?? null);
    set((s) => ({
      teams: s.teams.map((x) => (x.id === id ? { ...x, privacy, parentId: nextParent } : x)),
    }));
    await guard(core(`/records/teams/${id}`, { privacy, parent_id: nextParent }, 'PATCH'), set, get);
  },

  setParent: async (id, parentId) => {
    set((s) => ({ teams: s.teams.map((x) => (x.id === id ? { ...x, parentId } : x)) }));
    await guard(core(`/records/teams/${id}`, { parent_id: parentId }, 'PATCH'), set, get);
  },

  setArchived: async (id, archived) => {
    set((s) => ({ teams: s.teams.map((x) => (x.id === id ? { ...x, archived } : x)) }));
    await guard(core(`/records/teams/${id}`, { archived }, 'PATCH'), set, get);
  },

  deleteTeam: async (id) => {
    // team_id is ON DELETE RESTRICT on all 12 owning tables, so this fails
    // whenever the team still holds anything. The caller checks emptiness first
    // and explains; guard() surfaces the error if it races.
    const removed = get().teams.find((s) => s.id === id);
    set((s) => ({ teams: s.teams.filter((x) => x.id !== id) }));
    const { ok } = await guard(core(`/records/teams/${id}`, {}, 'DELETE'), set, get);
    if (!ok && removed) set((s) => ({ teams: [...s.teams, removed] }));
  },

  /** Query current open assignments directly; null means any count failed. */
  countOpenAssignments: async (teamId, personId) => {
    const head = { count: 'exact' as const, head: true };
    const [rocks, tasks, issues, metrics] = await Promise.all([
      supabase.from('rocks').select('id', head).eq('team_id', teamId).eq('owner_id', personId)
        .not('status', 'in', '("done","cancelled","missed")'),
      supabase.from('tasks').select('id', head).eq('team_id', teamId).eq('owner_id', personId)
        .eq('status', 'open'),
      supabase.from('issues').select('id', head).eq('team_id', teamId).eq('owner_id', personId)
        .eq('status', 'open'),
      supabase.from('metrics').select('id', head).eq('team_id', teamId).eq('owner_id', personId),
    ]);
    const parts = [rocks, tasks, issues, metrics];
    if (parts.some((p) => p.error)) return null;
    return parts.reduce((sum, p) => sum + (p.count ?? 0), 0);
  },

  unassignInTeam: async (teamId, personId) => {
    const unassigned = await coreApi(`/teams/${teamId}/unassign`, { person_id: personId });
    if (unassigned.error) return false;

    // Reflect the bounded change locally rather than refetching the workspace.
    useOperatingStore.setState((s) => {
      const clear = <T extends { teamId?: string; ownerId?: string }>(list: T[]) =>
        list.map((x) => (x.teamId === teamId && x.ownerId === personId ? { ...x, ownerId: undefined } : x));
      return { rocks: clear(s.rocks), tasks: clear(s.tasks), issues: clear(s.issues), metrics: clear(s.metrics) };
    });
    return true;
  },

  addMember: async (teamId, userId, role) => {
    const { ok, data: created } = await guard(
      core('/records/team-members', { team_id: teamId, user_id: userId, role }),
      set, get,
    );
    if (ok && created) set((s) => ({ members: [...s.members, memberFromRow(created)] }));
  },

  setMemberRole: async (memberId, role) => {
    set((s) => ({ members: s.members.map((m) => (m.id === memberId ? { ...m, role } : m)) }));
    await guard(core(`/records/team-members/${memberId}`, { role }, 'PATCH'), set, get);
  },

  removeMember: async (memberId) => {
    const removed = get().members.find((m) => m.id === memberId);
    set((s) => ({ members: s.members.filter((m) => m.id !== memberId) }));
    const { ok } = await guard(core(`/records/team-members/${memberId}`, {}, 'DELETE'), set, get);
    if (!ok && removed) set((s) => ({ members: [...s.members, removed] }));
  },
}));
